import type {
  ColumnDefTemplate,
  CellContext,
  CellData,
  RowData,
} from '@tanstack/react-table';
import { memo } from 'react';

import {
  adjustFractionDigits,
  type ConvertibleUnit,
  format,
  formatCurrency,
  formatDate as formatDatetime,
  type FormatDateOptions,
  formatLong,
  type FormatOptions,
  type Unit,
  units,
} from '@dynatrace-sdk/units';

import {
  DATETIME_DEFAULT_FORMAT_OPTIONS,
  formatCurrencyDefault,
  formatDatetimeDefault,
} from '../../../../core/utils/default-formatters.js';
import { devLog } from '../../../../core/utils/logging.js';
import {
  coerceDatetimeCellValue,
  coerceLongCellValue,
  coerceNumericCellValue,
} from '../../../utils/coerce-cell-value.js';
import type {
  DataTableDisplayColumnDef,
  DataTableRowData,
} from '../../public.api.js';
import type { TDataTableFeatures } from '../types/features.types.js';
import type {
  DataTableDatetimeDisplayMode,
  FormatterCurrencyOptions,
} from './cell-formatter-types.js';

/**
 * Format options for each `DataTableDatetimeDisplayMode` shorthand.
 * @internal
 */
const DATETIME_FORMAT_OPTIONS: Record<
  DataTableDatetimeDisplayMode,
  FormatDateOptions
> = {
  // e.g. "12/21/2024" for US English locale
  date: {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  },
  // e.g. "5:45:30 PM" for US English locale
  time: {
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
  },
  // e.g. "12/21/2024, 5:45:30 PM" for US English locale
  datetime: DATETIME_DEFAULT_FORMAT_OPTIONS,
};

/**
 * Typeguard to check if a formatter value is a `DataTableDatetimeDisplayMode` shorthand string.
 * @internal
 */
function isDatetimeShorthand(
  formatter: unknown,
): formatter is DataTableDatetimeDisplayMode {
  return (
    formatter === 'date' || formatter === 'time' || formatter === 'datetime'
  );
}

/**
 * Resolves the effective `FormatDateOptions` for a datetime column.
 * Accepts either a `FormatDateOptions` object, a `DataTableDatetimeDisplayMode`
 * shorthand string, or `undefined`.
 * @internal
 */
function resolveDatetimeFormatter(
  formatter: FormatDateOptions | DataTableDatetimeDisplayMode | undefined,
): FormatDateOptions | undefined {
  if (!formatter) {
    return undefined;
  }
  if (isDatetimeShorthand(formatter)) {
    return DATETIME_FORMAT_OPTIONS[formatter];
  }
  return formatter;
}

/** Memoized renderer for datetime formats */
const ColumnCellDatetimeFormatMemo = memo(
  // oxlint-disable-next-line typescript/no-explicit-any
  (props: { value: any; formatter?: FormatDateOptions }) => {
    const date = coerceDatetimeCellValue(props.value);
    return date !== null ? formatDatetime(date, props.formatter) : '';
  },
);

/** Memoized renderer for currency formats */
const ColumnCellCurrencyFormatMemo = memo(
  // oxlint-disable-next-line typescript/no-explicit-any
  (props: { value: any; formatter?: FormatterCurrencyOptions }) => {
    const num = coerceNumericCellValue(props.value);
    if (num === null) {
      return '';
    }

    const { currency, ...options } = props.formatter ?? {};
    if (!currency) {
      // no currency (e.g. 'EUR', 'USD') provided → default format without currency symbol
      return formatCurrencyDefault(num, options);
    }

    return formatCurrency(num, currency, options);
  },
);

/** Memoized renderer for fallback formats */
const ColumnCellFormatMemo = memo(
  // oxlint-disable-next-line typescript/no-explicit-any
  (props: { value: any; formatter?: FormatDateOptions }) => {
    const num = coerceNumericCellValue(props.value);
    return num !== null ? format(num, props.formatter) : '';
  },
);

const ColumnCellFormatLongMemo = memo(
  (props: {
    value: string | number | bigint;
    formatter?: Pick<
      FormatOptions<Unit, ConvertibleUnit>,
      | 'locale'
      | 'maximumFractionDigits'
      | 'maximumSignificantDigits'
      | 'minimumFractionDigits'
      | 'minimumSignificantDigits'
      | 'useGrouping'
    >;
  }) => {
    return formatLong(props.value, props.formatter);
  },
);

/**
 * Function that formats the cell value based on the formatter specified in the column definition
 * @param columnDef - the table column definition
 *
 * @returns the formatted cell value
 */
function formatColumnCell<
  TData extends RowData,
  TValue extends CellData = CellData,
>(
  columnDef: DataTableDisplayColumnDef<TData, TValue>,
):
  | ColumnDefTemplate<CellContext<TDataTableFeatures, TData, TValue>>
  | undefined {
  if (
    columnDef.columnType === 'sparkline' ||
    columnDef.columnType === 'meterbar' ||
    columnDef.columnType === 'gantt' ||
    columnDef.columnType === 'log-content' ||
    columnDef.columnType === 'markdown'
  ) {
    return;
  }

  // DEPRECATION: [APPDEV-16044] BREAKING-CHANGE: Remove 'date' columnType.
  if (columnDef.columnType === 'date' || columnDef.columnType === 'datetime') {
    const resolvedFormatter = resolveDatetimeFormatter(columnDef.formatter);
    return ({ getValue }) => (
      <ColumnCellDatetimeFormatMemo
        value={getValue()}
        formatter={resolvedFormatter}
      />
    );
  }

  if (columnDef.columnType === 'currency') {
    return ({ getValue }) => (
      <ColumnCellCurrencyFormatMemo
        value={getValue()}
        formatter={columnDef.formatter}
      />
    );
  }

  if (columnDef.columnType === 'long') {
    return ({ getValue }) => {
      const coercedValue = coerceLongCellValue(getValue());
      if (coercedValue !== null) {
        return (
          <ColumnCellFormatLongMemo
            value={coercedValue}
            formatter={columnDef.formatter}
          />
        );
      }
      return null;
    };
  }

  return ({ getValue }) => (
    <ColumnCellFormatMemo value={getValue()} formatter={columnDef.formatter} />
  );
}

/**
 * Updates the cell renderer with the formatter from the column definition
 * @param columnDef - column definition prop
 *
 * @returns the column definition with the formatter applied
 * @internal
 */
export function applyColumnFormatters<
  TData extends RowData,
  TValue extends CellData = CellData,
>(
  columnDef: DataTableDisplayColumnDef<TData, TValue>,
): {
  cell?: ColumnDefTemplate<CellContext<TDataTableFeatures, TData, TValue>>;
} {
  if (
    columnDef.columnType === 'sparkline' ||
    columnDef.columnType === 'meterbar' ||
    columnDef.columnType === 'gantt' ||
    columnDef.columnType === 'log-content' ||
    columnDef.columnType === 'markdown'
  ) {
    return {};
  }

  if (columnDef.formatter) {
    return {
      cell: formatColumnCell(columnDef),
    };
  }

  return {};
}

/**
 * Formats the given value based on formatter options from the column definition.
 * For 'sparkline', 'meterbar', 'gantt', and 'log-content', returns an empty string.
 * If there is no other formatter possible then returns a stringified value.
 * @param value - the cell value to format.
 * @param column - column definition prop where `columnType` and `formatter` are relevant.
 *
 * @returns the formatted cell value
 * @internal
 */
export function formatCellValue<TData extends DataTableRowData, TValue>(
  value: TValue,
  column: DataTableDisplayColumnDef<TData, TValue>,
): string {
  if (
    column.columnType === 'sparkline' ||
    column.columnType === 'meterbar' ||
    column.columnType === 'gantt' ||
    column.columnType === 'log-content' ||
    column.columnType === 'markdown'
  ) {
    return '';
  }

  if (value === null || value === undefined) {
    devLog(`The value '${value}' can not be formatted!`);
    return '';
  }

  const cleanedValue = coerceNumericCellValue(value);

  if (
    cleanedValue === null &&
    (column.columnType === 'number' ||
      column.columnType === 'bit' ||
      column.columnType === 'currency')
  ) {
    devLog(`The value '${value}' can not be formatted!`);
    return '';
  }

  if (
    column.formatter &&
    // DEPRECATION: [APPDEV-16044] BREAKING-CHANGE: Remove 'date' columnType.
    column.columnType !== 'date' &&
    column.columnType !== 'datetime' &&
    column.columnType !== 'currency' &&
    typeof cleanedValue === 'number'
  ) {
    const formatOptions = column.formatter;
    return format(cleanedValue, formatOptions);
  }

  if (column.columnType === 'bit' && typeof cleanedValue === 'number') {
    const formatOptions = { input: units.data.bit };
    return format(cleanedValue, formatOptions);
  }

  if (column.columnType === 'number' && typeof cleanedValue === 'number') {
    const formatOptions = column.formatter;
    if (formatOptions) {
      return format(cleanedValue, formatOptions);
    }
    return adjustFractionDigits(cleanedValue);
  }

  if (column.columnType === 'long') {
    const coerced = coerceLongCellValue(value);
    if (coerced !== null) {
      return formatLong(coerced, column.formatter);
    }
    return '';
  }

  // DEPRECATION: [APPDEV-16044] BREAKING-CHANGE: Remove 'date' columnType
  if (column.columnType === 'date' || column.columnType === 'datetime') {
    const date = coerceDatetimeCellValue(value);
    if (date !== null) {
      const resolvedFormatter = resolveDatetimeFormatter(column.formatter);
      if (resolvedFormatter) {
        return formatDatetime(date, resolvedFormatter);
      } else {
        return formatDatetimeDefault(date);
      }
    }
    return '';
  }

  if (column.columnType === 'currency' && typeof cleanedValue === 'number') {
    const formatOptions = column.formatter;
    const { currency, ...remainingOptions } = formatOptions ?? {};
    if (!currency) {
      // no currency (e.g. 'EUR', 'USD') provided → default format without currency symbol
      return formatCurrencyDefault(cleanedValue, remainingOptions);
    }

    return formatCurrency(cleanedValue, currency, remainingOptions);
  }

  devLog(`The value '${value}' can not be formatted!`);
  return String(value);
}
